// Three.js particle systems for the ASCEND microsite.
//
// Smoke renders on its own canvas so CSS blur can be applied without
// affecting embers/fire. Embers and fire share the main fx canvas.
//
// Public API:
//   ASCEND_FX.createSmokeScene(canvas) → { start, stop, setIntensity, setBlur, setDensity }
//   ASCEND_FX.createEmberFireScene(canvas) → { start, stop, setProgress }
//
// All textures generated in-canvas at load — no asset deps.

(function () {
  const NS = (window.ASCEND_FX = window.ASCEND_FX || {});

  // ─── Procedural sprite textures ─────────────────────────────────────────
  function makeRadialTexture({ size = 256, stops, power = 2 }) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(size, size);
    const cx = size / 2, cy = size / 2, r = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - cx, dy = y - cy;
        const d = Math.sqrt(dx * dx + dy * dy) / r;
        const t = Math.max(0, 1 - d);
        const a = Math.pow(t, power);
        const i = (y * size + x) * 4;
        let rC = 255, gC = 255, bC = 255;
        for (let s = 0; s < stops.length - 1; s++) {
          const s0 = stops[s], s1 = stops[s + 1];
          if (a >= s1[0] && a <= s0[0]) {
            const lt = (a - s1[0]) / (s0[0] - s1[0]);
            rC = s1[1] + (s0[1] - s1[1]) * lt;
            gC = s1[2] + (s0[2] - s1[2]) * lt;
            bC = s1[3] + (s0[3] - s1[3]) * lt;
            break;
          }
        }
        img.data[i] = rC;
        img.data[i + 1] = gC;
        img.data[i + 2] = bC;
        img.data[i + 3] = a * 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  // Smoke: very soft, power 1.2 for a wide feathered edge — this blurs
  // beautifully when the canvas gets CSS filter:blur applied.
  const smokeTex = () => makeRadialTexture({
    size: 512, power: 1.2,
    stops: [
      [1.0, 210, 205, 200],
      [0.6, 160, 154, 148],
      [0.25, 80,  75,  70],
      [0.0,  12,  10,   8],
    ],
  });

  // Secondary smoke: warmer tint (near-fire heat haze)
  const smokeWarmTex = () => makeRadialTexture({
    size: 512, power: 1.3,
    stops: [
      [1.0, 220, 195, 170],
      [0.55, 160, 130, 100],
      [0.2,   70,  52,  36],
      [0.0,   10,   6,   2],
    ],
  });

  const emberTex = () => makeRadialTexture({
    size: 128, power: 2.2,
    stops: [
      [1.0, 255, 255, 230],
      [0.55, 255, 180, 80],
      [0.2,  255, 80, 20],
      [0.0,  120, 20, 0],
    ],
  });

  // Flame teardrop texture: tall canvas, wide at base, pointed at top
  function makeFlameTexture() {
    const w = 128, h = 200;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(w, h);
    const cx = w / 2;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        // ny: +1 at bottom, -1 at top (flame base is bottom)
        const ny = 1.0 - (y / h) * 2.0;
        const nx = (x - cx) / (w * 0.5);
        // Taper: full width at base, zero at tip
        const taper = Math.max(0, (ny + 1.0) / 2.0);
        const rx = 0.55 * Math.sqrt(taper);
        const ry = 0.82;
        const yOff = 0.18; // shift centre downward so tip points up
        const dx = nx / Math.max(rx, 0.01);
        const dy = (ny + yOff) / ry;
        const d = Math.sqrt(dx * dx + dy * dy);
        const a = Math.pow(Math.max(0, 1 - d), 1.6);
        if (a < 0.01) { img.data[(y * w + x) * 4 + 3] = 0; continue; }
        const i4 = (y * w + x) * 4;
        // colour: white core → yellow → orange → red edge
        const r = 255;
        const g = Math.round(a > 0.65 ? 240 : a > 0.35 ? 140 : 60);
        const b = Math.round(a > 0.65 ? 180 : a > 0.35 ? 20 : 5);
        img.data[i4]   = r;
        img.data[i4+1] = g;
        img.data[i4+2] = b;
        img.data[i4+3] = Math.round(a * 255);
      }
    }
    ctx.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  const fireTex = makeFlameTexture;

  // ─── Particle layer factory ────────────────────────────────────────────
  function ParticleLayer(opts) {
    const N = opts.count;
    const positions  = new Float32Array(N * 3);
    const velocities = new Float32Array(N * 3);
    const ages  = new Float32Array(N);
    const ttls  = new Float32Array(N);
    const sizes = new Float32Array(N);
    const seeds = new Float32Array(N);

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('aSize',    new THREE.BufferAttribute(sizes, 1));
    geom.setAttribute('aAge',     new THREE.BufferAttribute(ages, 1));
    geom.setAttribute('aTtl',     new THREE.BufferAttribute(ttls, 1));

    const uniforms = {
      uTex:      { value: opts.texture },
      uColor:    { value: opts.color },
      uOpacity:  { value: opts.opacity },
      uPxScale:  { value: 1 },
      uFadeIn:   { value: opts.fadeIn  ?? 0.15 },
      uFadeOut:  { value: opts.fadeOut ?? 0.5  },
      uShrink:   { value: opts.shrink  ?? 0    },
      uIntensity:{ value: opts.intensity ?? 1  },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite:  !!opts.depthWrite,
      depthTest:   false,
      blending:    opts.blending,
      vertexShader: /* glsl */`
        attribute float aSize;
        attribute float aAge;
        attribute float aTtl;
        uniform float uPxScale;
        uniform float uShrink;
        varying float vT;
        void main() {
          float t = clamp(aAge / aTtl, 0.0, 1.0);
          vT = t;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          float shrink = mix(1.0, 1.0 - uShrink, t);
          gl_PointSize = aSize * uPxScale * shrink * (300.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: opts.fragmentShader ?? /* glsl */`
        uniform sampler2D uTex;
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uFadeIn;
        uniform float uFadeOut;
        uniform float uIntensity;
        varying float vT;
        void main() {
          vec4 tex = texture2D(uTex, gl_PointCoord);
          float aIn  = smoothstep(0.0, uFadeIn,  vT);
          float aOut = 1.0 - smoothstep(1.0 - uFadeOut, 1.0, vT);
          float env  = aIn * aOut;
          float a = tex.a * env * uOpacity * uIntensity;
          if (a < 0.008) discard;
          gl_FragColor = vec4(tex.rgb * uColor, a);
        }
      `,
    });

    const points = new THREE.Points(geom, mat);
    points.frustumCulled = false;

    function respawn(i, initial = false) {
      const spread = opts.spread;
      // Power-distributed spawn X: particles cluster near center with organic outliers
      const rx = (Math.random() - 0.5) * 2;
      const rz = (Math.random() - 0.5) * 2;
      positions[i * 3]     = Math.sign(rx) * Math.pow(Math.abs(rx), 0.55) * spread.x;
      positions[i * 3 + 1] = opts.spawnY + (Math.random() - 0.5) * (opts.spawnYJitter || 0);
      positions[i * 3 + 2] = Math.sign(rz) * Math.pow(Math.abs(rz), 0.55) * spread.z;
      // Horizontal kick: random burst sideways for some particles
      const kick = opts.kickX || 0;
      velocities[i * 3]     = (Math.random() - 0.5) * (opts.driftXZ || 0)
                             + (Math.random() < 0.2 ? (Math.random() - 0.5) * kick : 0);
      // Rise speed varies widely for organic feel
      velocities[i * 3 + 1] = opts.rise * (0.35 + Math.pow(Math.random(), 0.7) * 1.3);
      velocities[i * 3 + 2] = (Math.random() - 0.5) * (opts.driftXZ || 0);
      ttls[i]  = opts.ageMax * (0.4 + Math.random() * 1.2);
      ages[i]  = initial ? Math.random() * ttls[i] : 0;
      sizes[i] = opts.size * (1 - opts.sizeJitter + Math.pow(Math.random(), 0.6) * opts.sizeJitter * 2);
      seeds[i] = Math.random() * 1000;
    }

    function seedAll() {
      for (let i = 0; i < N; i++) respawn(i, true);
      geom.attributes.position.needsUpdate = true;
      geom.attributes.aSize.needsUpdate    = true;
      geom.attributes.aAge.needsUpdate     = true;
      geom.attributes.aTtl.needsUpdate     = true;
    }

    function update(dt, t, wind) {
      const turb      = opts.turbulence || 0;
      const turbFreq  = opts.turbFreq   || 0.5;
      const windX     = wind ? wind.x : 0;
      const windZ     = wind ? wind.z : 0;
      const pAttr = geom.attributes.position.array;
      const aAttr = geom.attributes.aAge.array;
      const sAttr = geom.attributes.aSize.array;
      const tAttr = geom.attributes.aTtl.array;
      for (let i = 0; i < N; i++) {
        ages[i] += dt;
        if (ages[i] > ttls[i] || pAttr[i * 3 + 1] > opts.killY) {
          respawn(i);
        }
        // Multi-frequency turbulence for organic swirl
        const t1 = Math.sin(t * turbFreq       + seeds[i]) * turb;
        const t2 = Math.sin(t * turbFreq * 2.3 + seeds[i] * 0.7) * turb * 0.35;
        const t3 = Math.cos(t * turbFreq * 1.3 + seeds[i]) * turb;
        const t4 = Math.cos(t * turbFreq * 3.1 + seeds[i] * 1.3) * turb * 0.25;
        pAttr[i * 3]     += (velocities[i * 3]     + windX) * dt + (t1 + t2) * dt;
        pAttr[i * 3 + 1] += velocities[i * 3 + 1] * dt;
        pAttr[i * 3 + 2] += (velocities[i * 3 + 2] + windZ) * dt + (t3 + t4) * dt;
        aAttr[i] = ages[i];
        sAttr[i] = sizes[i];
        tAttr[i] = ttls[i];
      }
      geom.attributes.position.needsUpdate = true;
      geom.attributes.aAge.needsUpdate     = true;
      geom.attributes.aSize.needsUpdate    = true;
      geom.attributes.aTtl.needsUpdate     = true;
    }

    seedAll();
    return {
      points,
      update,
      setIntensity(v) { uniforms.uIntensity.value = v; },
      setPxScale(v)   { uniforms.uPxScale.value = v; },
      get uniforms()  { return uniforms; },
    };
  }

  // ─── Smoke scene (separate canvas so CSS blur can be applied) ─────────
  NS.createSmokeScene = function (canvas, opts) {
    const mobile = opts && opts.mobile;
    const renderer = new THREE.WebGLRenderer({
      canvas, alpha: true, antialias: false, powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(mobile ? 1 : Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
    camera.position.set(0, 6, 22);
    camera.lookAt(0, 6, 0);

    // Layer A — vast slow atmospheric haze, very low opacity so overlap builds the mass
    const bgSmoke = ParticleLayer({
      count: mobile ? 55 : 130,
      texture: smokeTex(),
      color: new THREE.Color(0.86, 0.84, 0.82),
      blending: THREE.NormalBlending,
      opacity: 0.18,
      size: 26, sizeJitter: 0.4,
      spread: { x: 30, z: 8 },
      spawnY: -6, spawnYJitter: 10,
      killY: 22,
      rise: 0.17,
      driftXZ: 0.12,
      turbulence: 0.25, turbFreq: 0.10,
      ageMax: 34,
      fadeIn: 0.20, fadeOut: 0.45,
      shrink: -0.80,
    });
    scene.add(bgSmoke.points);

    // Layer B — mid haze, slightly warmer, fills the middle volume
    const midSmoke = ParticleLayer({
      count: mobile ? 80 : 200,
      texture: smokeTex(),
      color: new THREE.Color(0.91, 0.89, 0.87),
      blending: THREE.NormalBlending,
      opacity: 0.13,
      size: 18, sizeJitter: 0.45,
      spread: { x: 22, z: 5 },
      spawnY: -3, spawnYJitter: 6,
      killY: 19,
      rise: 0.25,
      driftXZ: 0.15,
      turbulence: 0.30, turbFreq: 0.14,
      ageMax: 24,
      fadeIn: 0.22, fadeOut: 0.48,
      shrink: -0.65,
    });
    scene.add(midSmoke.points);

    // Layer C — warm near-base haze, very diffuse
    const fgSmoke = ParticleLayer({
      count: mobile ? 60 : 150,
      texture: smokeWarmTex(),
      color: new THREE.Color(0.80, 0.72, 0.64),
      blending: THREE.NormalBlending,
      opacity: 0.11,
      size: 13, sizeJitter: 0.5,
      spread: { x: 16, z: 4 },
      spawnY: -7, spawnYJitter: 5,
      killY: 14,
      rise: 0.34,
      driftXZ: 0.18,
      turbulence: 0.40, turbFreq: 0.18,
      ageMax: 17,
      fadeIn: 0.25, fadeOut: 0.50,
      shrink: -0.50,
    });
    scene.add(fgSmoke.points);

    let _densityScale = 1.0; // 0..1 slider control

    function resize() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      const px = renderer.getPixelRatio();
      bgSmoke.setPxScale(px);
      midSmoke.setPxScale(px);
      fgSmoke.setPxScale(px);
    }
    resize();
    new ResizeObserver(resize).observe(canvas);

    const ctl = {
      _t0: performance.now(), _last: performance.now(), _running: false,
      _scrollIntensity: 1.0,

      // Called by scroll driver with 0..1 progress
      setScrollIntensity(v) {
        this._scrollIntensity = v;
        this._applyIntensity();
      },

      // Called by slider with 0..1 density
      setDensity(d) {
        _densityScale = d;
        this._applyIntensity();
      },

      _applyIntensity() {
        const base = this._scrollIntensity * _densityScale;
        bgSmoke.setIntensity(base * 0.9);
        midSmoke.setIntensity(base * 1.0);
        fgSmoke.setIntensity(base * 0.75);
        camera.position.y = THREE.MathUtils.lerp(8, 0, 1 - this._scrollIntensity);
        camera.lookAt(0, camera.position.y, 0);
      },

      // Apply CSS blur to the canvas element
      setBlur(px) {
        canvas.style.filter = `blur(${px}px)`;
      },

      start() {
        if (this._running) return;
        this._running = true;
        const tick = () => {
          if (!this._running) return;
          const now = performance.now();
          const dt  = Math.min(0.05, (now - this._last) / 1000);
          this._last = now;
          const t = (now - this._t0) / 1000;
          // Slow organic wind drift
          const wind = {
            x: Math.sin(t * 0.07) * 0.12 + Math.sin(t * 0.19) * 0.05,
            z: Math.cos(t * 0.11) * 0.06,
          };
          bgSmoke.update(dt, t, wind);
          midSmoke.update(dt, t, wind);
          fgSmoke.update(dt, t, wind);
          renderer.render(scene, camera);
          this._raf = requestAnimationFrame(tick);
        };
        this._raf = requestAnimationFrame(tick);
      },
      stop() {
        this._running = false;
        if (this._raf) cancelAnimationFrame(this._raf);
      },
    };

    ctl._applyIntensity();
    return ctl;
  };

  // ─── Ember + Fire scene (main canvas, no blur) ────────────────────────
  NS.createEmberFireScene = function (canvas, opts) {
    const mobile = opts && opts.mobile;
    const renderer = new THREE.WebGLRenderer({
      canvas, alpha: true, antialias: false, powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(mobile ? 1 : Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
    camera.position.set(0, 0, 22);
    camera.lookAt(0, 0, 0);

    const embers = ParticleLayer({
      count: mobile ? 180 : 420,
      texture: emberTex(),
      color: new THREE.Color(1.0, 0.82, 0.45),
      blending: THREE.AdditiveBlending,
      opacity: 1.4,
      size: 0.5, sizeJitter: 0.85,
      spread: { x: 12, z: 3.5 },
      spawnY: -10, spawnYJitter: 4,
      killY: 16,
      rise: 4.2,
      driftXZ: 0.8,
      kickX: 2.2,
      turbulence: 2.6, turbFreq: 1.2,
      ageMax: 5.5,
      fadeIn: 0.08, fadeOut: 0.55,
      shrink: 0.65,
    });
    scene.add(embers.points);

    // Fine sparks — tiny, fast, erratic
    const sparks = ParticleLayer({
      count: mobile ? 110 : 300,
      texture: emberTex(),
      color: new THREE.Color(1.0, 0.96, 0.78),
      blending: THREE.AdditiveBlending,
      opacity: 0.85,
      size: 0.18, sizeJitter: 0.92,
      spread: { x: 9, z: 2.5 },
      spawnY: -10, spawnYJitter: 5,
      killY: 20,
      rise: 7.5,
      driftXZ: 1.4,
      kickX: 3.5,
      turbulence: 4.0, turbFreq: 3.2,
      ageMax: 2.8,
      fadeIn: 0.05, fadeOut: 0.38,
      shrink: 0.85,
    });
    scene.add(sparks.points);

    const flameShader = /* glsl */`
      uniform sampler2D uTex;
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uFadeIn;
      uniform float uFadeOut;
      uniform float uIntensity;
      varying float vT;
      void main() {
        vec2 uv = gl_PointCoord;
        // Taper: narrow at top (uv.y=0), wide at base (uv.y=1)
        float taper = clamp(0.18 + 0.82 * uv.y, 0.18, 1.0);
        vec2 flameUV = vec2((uv.x - 0.5) / taper + 0.5, uv.y);
        flameUV = clamp(flameUV, 0.0, 1.0);
        vec4 tex = texture2D(uTex, flameUV);
        float aIn  = smoothstep(0.0, uFadeIn,  vT);
        float aOut = 1.0 - smoothstep(1.0 - uFadeOut, 1.0, vT);
        float env  = aIn * aOut;
        float a = tex.a * env * uOpacity * uIntensity;
        if (a < 0.008) discard;
        gl_FragColor = vec4(tex.rgb * uColor, a);
      }
    `;

    const fire = ParticleLayer({
      count: mobile ? 250 : 700,
      texture: fireTex(),
      color: new THREE.Color(1.0, 0.72, 0.35),
      blending: THREE.AdditiveBlending,
      opacity: 1.1,
      size: 2.8, sizeJitter: 0.65,
      spread: { x: 5, z: 1.5 },
      spawnY: -13, spawnYJitter: 1.5,
      killY: -1,
      rise: 8.0,
      driftXZ: 0.55,
      turbulence: 2.8, turbFreq: 2.8,
      ageMax: 1.4,
      fadeIn: 0.15, fadeOut: 0.5,
      shrink: 0.8,
      fragmentShader: flameShader,
    });
    scene.add(fire.points);

    // Flame tongues — fast, thin, high-turbulence upper tips
    const flameTongues = ParticleLayer({
      count: mobile ? 100 : 300,
      texture: fireTex(),
      color: new THREE.Color(1.0, 0.90, 0.60),
      blending: THREE.AdditiveBlending,
      opacity: 0.7,
      size: 1.4, sizeJitter: 0.8,
      spread: { x: 3.5, z: 1 },
      spawnY: -11, spawnYJitter: 1.0,
      killY: 1,
      rise: 11.0,
      driftXZ: 0.9,
      turbulence: 4.5, turbFreq: 4.0,
      ageMax: 0.9,
      fadeIn: 0.1, fadeOut: 0.45,
      shrink: 0.9,
      fragmentShader: flameShader,
    });
    scene.add(flameTongues.points);

    const glowMat = new THREE.SpriteMaterial({
      map: fireTex(),
      color: 0xff8833,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      opacity: 0,
    });
    const glow = new THREE.Sprite(glowMat);
    glow.scale.set(14, 8, 1);
    glow.position.set(0, -12.5, 0);
    scene.add(glow);

    function resize() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      const px = renderer.getPixelRatio();
      embers.setPxScale(px);
      sparks.setPxScale(px);
      fire.setPxScale(px);
      flameTongues.setPxScale(px);
    }
    resize();
    new ResizeObserver(resize).observe(canvas);

    const ctl = {
      _t0: performance.now(), _last: performance.now(), _running: false,

      setProgress(p) {
        p = Math.max(0, Math.min(1, p));
        const e = Math.sin(Math.min(p, 0.9) / 0.9 * Math.PI * 0.5);
        embers.setIntensity(0.15 + e * 1.1);
        sparks.setIntensity((0.08 + e * 0.7));
        const f = Math.max(0, (p - 0.4) / 0.6);
        fire.setIntensity(Math.pow(f, 1.4) * 1.1);
        flameTongues.setIntensity(Math.pow(f, 1.6) * 0.85);
        glowMat.opacity = Math.pow(f, 2.0) * 0.35;
        camera.position.y = THREE.MathUtils.lerp(6, -8, p);
        camera.lookAt(0, camera.position.y - 0.5, 0);
      },

      start() {
        if (this._running) return;
        this._running = true;
        const tick = () => {
          if (!this._running) return;
          const now = performance.now();
          const dt  = Math.min(0.05, (now - this._last) / 1000);
          this._last = now;
          const t = (now - this._t0) / 1000;
          // Slow organic wind that shifts direction over time
          const wind = {
            x: Math.sin(t * 0.09) * 0.18 + Math.sin(t * 0.23) * 0.07,
            z: Math.cos(t * 0.13) * 0.08,
          };
          embers.update(dt, t, wind);
          sparks.update(dt, t, wind);
          fire.update(dt, t, wind);
          flameTongues.update(dt, t, wind);
          renderer.render(scene, camera);
          this._raf = requestAnimationFrame(tick);
        };
        this._raf = requestAnimationFrame(tick);
      },
      stop() {
        this._running = false;
        if (this._raf) cancelAnimationFrame(this._raf);
      },
    };

    ctl.setProgress(0);
    return ctl;
  };

})();
